import apiClient from './client';

export interface FirmConversation {
  clientId: string;
  displayName: string;
  managerName?: string | null;
  phone?: string | null;
  email?: string | null;
  appAccessEnabled: boolean;
  lastMessage?: string | null;
  lastAt?: string | null;
  lastDirection?: 'inbound' | 'outbound' | null;
  total: number;
  firmUnread: number;
}

export interface FirmClientMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  attachmentFileId?: string | null;
  attachmentName?: string | null;
  attachmentContentType?: string | null;
  attachmentUrl?: string | null;
  senderName?: string | null;
  readAt?: string | null;
  firmReadAt?: string | null;
  createdAt: string;
}

export interface FirmConversationDetail {
  client: { id: string; displayName: string; managerName?: string | null; phone?: string | null; email?: string | null; appAccessEnabled: boolean };
  messages: FirmClientMessage[];
}

export async function setAppAccess(clientId: string, enabled: boolean): Promise<void> {
  await apiClient.put(`/clients/${clientId}`, { appAccessEnabled: enabled });
}

export async function listConversations(): Promise<FirmConversation[]> {
  const r = await apiClient.get<{ conversations: FirmConversation[] }>('/firm/client-messages/conversations');
  return r.data.conversations;
}

export async function getConversation(clientId: string): Promise<FirmConversationDetail> {
  const r = await apiClient.get<FirmConversationDetail>(`/firm/client-messages/${clientId}`);
  return r.data;
}

export interface MessageAttachment {
  s3Key: string;
  filename: string;
  contentType: string;
  fileSizeBytes: number;
}

/** Upload a file/voice attachment for a single-client message, returns its ref. */
export async function uploadMessageFile(clientId: string, file: File): Promise<MessageAttachment> {
  const r = await apiClient.post<{ uploadUrl: string; s3Key: string; filename: string }>(
    `/firm/client-messages/${clientId}/upload-url`,
    { filename: file.name, contentType: file.type, fileSizeBytes: file.size },
  );
  await fetch(r.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  return { s3Key: r.data.s3Key, filename: r.data.filename, contentType: file.type, fileSizeBytes: file.size };
}

export async function sendMessage(clientId: string, body: string, attachment?: MessageAttachment): Promise<{ message: FirmClientMessage }> {
  const r = await apiClient.post<{ message: FirmClientMessage }>(`/firm/client-messages/${clientId}`, { body, attachment });
  return r.data;
}

export async function markRead(clientId: string): Promise<void> {
  await apiClient.put(`/firm/client-messages/${clientId}/read`, {});
}

export interface BroadcastAttachment {
  s3Key: string;
  filename: string;
  contentType: string;
  fileSizeBytes: number;
}

/** Admin-only: get a presigned URL to upload a broadcast attachment, then PUT the file. */
export async function uploadBroadcastFile(file: File): Promise<BroadcastAttachment> {
  const r = await apiClient.post<{ uploadUrl: string; s3Key: string; filename: string; contentType: string }>(
    '/firm/client-messages/broadcast/upload-url',
    { filename: file.name, contentType: file.type, fileSizeBytes: file.size },
  );
  // PUT straight to S3 (presigned) - do NOT send auth headers to S3.
  await fetch(r.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  return { s3Key: r.data.s3Key, filename: r.data.filename, contentType: file.type, fileSizeBytes: file.size };
}

/** Admin-only: send one message (text and/or attachment) to every client. */
export async function broadcastMessage(
  body: string, target: 'app' | 'all' = 'app', attachment?: BroadcastAttachment,
): Promise<{ sent: number; target: string }> {
  const r = await apiClient.post<{ sent: number; target: string }>(
    '/firm/client-messages/broadcast', { body, target, attachment },
  );
  return r.data;
}
